import React, { memo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import MentionErrorBoundary from './MentionErrorBoundary';

const EditableMentionSpan = memo(({
  userId,
  displayName,
  onDelete,
  onClick,
  className = '',
  disabled = false
}) => {
  const navigate = useNavigate();
  
  // Handle mention click
  const handleClick = useCallback((e) => {
    // Only prevent default and stop propagation for actual navigation clicks
    // Allow cursor positioning clicks to bubble up
    if (e.detail === 2 || e.ctrlKey || e.metaKey) {
      // Double-click or Ctrl/Cmd+click - navigate to profile
      e.preventDefault();
      e.stopPropagation();
      
      if (disabled) return;
      
      if (onClick) {
        onClick(userId, displayName);
      } else {
        // Default behavior: navigate to user profile
        navigate(`/network/${userId}`);
      }
    }
    // Single clicks are allowed to bubble up for cursor positioning
  }, [userId, displayName, onClick, disabled, navigate]);
  
  // Handle mousedown to prevent text selection within mention
  const handleMouseDown = useCallback((e) => {
    if (disabled) return;
    
    // Prevent text selection within the mention span
    // but allow the event to bubble up for cursor positioning
    e.preventDefault();
  }, [disabled]);

  // Handle key events for deletion and navigation
  const handleKeyDown = useCallback((e) => {
    if (disabled) return;
    
    switch (e.key) {
      case 'Enter':
      case ' ':
        e.preventDefault();
        handleClick(e);
        break;
      case 'Backspace':
      case 'Delete':
        e.preventDefault();
        e.stopPropagation();
        if (onDelete) {
          onDelete();
        }
        break;
      case 'Escape':
        // Remove focus from mention
        e.target.blur();
        break;
      default:
        // For other keys, prevent default to avoid editing the mention
        if (e.key.length === 1) {
          e.preventDefault();
        }
        break;
    }
  }, [handleClick, onDelete, disabled]);
  
  // Handle deleted/invalid users
  if (!userId || !displayName) {
    return (
      <span className={`text-gray-500 dark:text-gray-400 ${className}`}>
        {displayName || 'Unknown User'}
      </span>
    );
  }
  
  return (
    <MentionErrorBoundary fallbackType="mention">
      <span
        className={`
          editable-mention inline text-sm font-medium
          text-purple-600 dark:text-purple-400
          underline decoration-purple-600 dark:decoration-purple-400
          cursor-pointer transition-all duration-200 ease-in-out
          hover:text-purple-700 dark:hover:text-purple-300
          hover:decoration-purple-700 dark:hover:decoration-purple-300
          hover:bg-purple-50 dark:hover:bg-purple-900/20
          focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-1
          rounded-sm px-0.5 -mx-0.5
          ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
          ${className}
        `}
        onClick={handleClick}
        onMouseDown={handleMouseDown}
        onKeyDown={handleKeyDown}
        role="button"
        tabIndex={disabled ? -1 : 0}
        aria-label={`Mentioned user: ${displayName}. Double-click to view profile, Backspace or Delete to remove mention.`}
        aria-describedby={`mention-help-${userId}`}
        aria-pressed="false"
        data-user-id={userId}
        data-display-name={displayName}
        data-mention="true"
        contentEditable="false"
        spellCheck="false"
        suppressContentEditableWarning={true}
      >
        @{displayName}
      </span>
      
      {/* Hidden help text for screen readers */}
      <span 
        id={`mention-help-${userId}`}
        className="sr-only"
      >
        User mention. Navigate with Tab, activate with Enter or Space, delete with Backspace.
      </span>
    </MentionErrorBoundary>
  );
});

EditableMentionSpan.displayName = 'EditableMentionSpan';

export default EditableMentionSpan;